import React ,{ useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import './ProductPage.css';
import premiumBadge from '../../assets/images/premium-account.png';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const PRODUCTS_PER_PAGE = 12;
const PRICE_MAX = 10000;

const CATEGORY_LABELS = {
  fruits: 'Fruits',
  vegetables: 'Vegetables',
  flowers: 'Flowers',
  herbs: 'Herbs',
  trees: 'Trees',
  others: 'Others',
};

const SELLER_TYPE_LABELS = {
  farmer: 'Farmer',
  verified: 'KYC Verified',
  trusted: 'Trusted',
};

const PRICE_PRESETS = [
  { label: 'Under ₹499', min: 0, max: 499 },
  { label: '₹500–₹1499', min: 500, max: 1499 },
  { label: '₹1500–₹2499', min: 1500, max: 2499 },
  { label: '₹2500–₹4999', min: 2500, max: 4999 },
  { label: 'Above ₹5000', min: 5000, max: 10000 },
];

const DEFAULT_CATEGORIES = () =>
  Object.fromEntries(Object.keys(CATEGORY_LABELS).map(k => [k, false]));

const DEFAULT_SELLER_TYPES = () =>
  Object.fromEntries(Object.keys(SELLER_TYPE_LABELS).map(k => [k, false]));

/* ─────────────────────────────────────────────
   Image URL helper - FIXED
───────────────────────────────────────────── */
const processImageUrl = (url) => {
  if (!url) return '/placeholder-plant.jpg'; // Return placeholder instead of null
  if (/^https?:\/\//.test(url) || /^data:image\//.test(url)) return url;
  const base = process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT || '';
  if (!base && url) return url; // If no base URL, return original
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
};

/* ─────────────────────────────────────────────
   Skeleton loader
───────────────────────────────────────────── */
const SkeletonGrid = () => (
  <div className="skeleton-grid">
    {Array.from({ length: 8 }).map((_, i) => (
      <div className="skeleton-card" key={i}>
        <div className="skeleton-img" />
        <div className="skeleton-body">
          <div className="skeleton-line" />
          <div className="skeleton-line short" />
          <div className="skeleton-line xshort" />
        </div>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   Filter panel content
───────────────────────────────────────────── */
const FilterPanel = ({
  priceRange, setPriceRange,
  categories, toggleCategory,
  sellerTypes, toggleSellerType,
  dateRange, setDateRange,
  clearFilters,
}) => (
  <>
    <div className="filter-section">
      <h4>Price Range</h4>
      <div className="range-inputs">
        <input
          type="range" min="0" max={PRICE_MAX}
          value={priceRange[0]}
          onChange={e => setPriceRange([+e.target.value, priceRange[1]])}
        />
        <input
          type="range" min="0" max={PRICE_MAX}
          value={priceRange[1]}
          onChange={e => setPriceRange([priceRange[0], +e.target.value])}
        />
      </div>
      <div className="range-values">
        <span>₹{priceRange[0].toLocaleString()}</span>
        <span>₹{priceRange[1].toLocaleString()}</span>
      </div>
      <div className="price-presets">
        {PRICE_PRESETS.map(({ label, min, max }) => (
          <button
            key={label}
            className={priceRange[0] === min && priceRange[1] === max ? 'active' : ''}
            onClick={() => setPriceRange([min, max])}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    <div className="filter-section">
      <h4>Category</h4>
      <div className="filter-options">
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <button
            key={key}
            className={`filter-option ${categories[key] ? 'active' : ''}`}
            onClick={() => toggleCategory(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    <div className="filter-section">
      <h4>Seller Type</h4>
      <div className="filter-options">
        {Object.entries(SELLER_TYPE_LABELS).map(([key, label]) => (
          <button
            key={key}
            className={`filter-option ${sellerTypes[key] ? 'active' : ''}`}
            onClick={() => toggleSellerType(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    <div className="filter-section">
      <h4>Date Added</h4>
      <div className="filter-options column">
        {[['all', 'All Time'], ['7days', 'Last 7 Days'], ['30days', 'Last 30 Days']].map(
          ([val, label]) => (
            <button
              key={val}
              className={`filter-option ${dateRange === val ? 'active' : ''}`}
              onClick={() => setDateRange(val)}
            >
              {label}
            </button>
          )
        )}
      </div>
    </div>
  </>
);

/* ─────────────────────────────────────────────
   Product Card
───────────────────────────────────────────── */
const ProductCard = React.memo(({ product, seller, onShare, onClick }) => {
  const navigate = useNavigate();

  const handleChat = useCallback(e => {
    e.stopPropagation();
    navigate(`/messages/${product.sellerId}`);
  }, [navigate, product.sellerId]);

  const handleSellerClick = useCallback(e => {
    e.stopPropagation();
    navigate(`/profile/${product.sellerId}`);
  }, [navigate, product.sellerId]);

  const handleShare = useCallback(e => {
    e.stopPropagation();
    onShare();
  }, [onShare]);

  // Get first valid image URL
  const imageUrl = product.imageUrls?.[0] || product.imageUrl || '/placeholder-plant.jpg';

  return (
    <div className="product-card" onClick={onClick}>
      <div className="product-image-container">
        <img
          src={imageUrl}
          alt={product.name || 'Product'}
          loading="lazy"
          decoding="async"
          onError={e => { e.target.src = '/placeholder-plant.jpg'; }}
        />
        {product.category && (
          <span className="product-category-badge">
            {CATEGORY_LABELS[product.category.toLowerCase()] || product.category}
          </span>
        )}
        <button className="share-button" aria-label="Share" onClick={handleShare}>
          <svg viewBox="0 0 24 24">
            <path d="M18 16.08c-.7 0-1.3.27-1.77.72l-7.12-4.11c.05-.15.09-.31.09-.47 0-.16-.04-.32-.09-.47l7.12-4.11c.47.45 1.07.72 1.77.72 1.54 0 2.8-1.26 2.8-2.8 0-1.54-1.26-2.8-2.8-2.8-1.54 0-2.8 1.26-2.8 2.8 0 .16.04.32.09.47l-7.12 4.11c-.47-.45-1.07-.72-1.77-.72-1.54 0-2.8 1.26-2.8 2.8 0 1.54 1.26 2.8 2.8 2.8.7 0 1.3-.27 1.77-.72l7.12 4.11c-.05.15-.09.31-.09.47 0 1.54 1.26 2.8 2.8 2.8 1.54 0 2.8-1.26 2.8-2.8 0-1.54-1.26-2.8-2.8-2.8z"/>
          </svg>
        </button>
      </div>

      <div className="product-details">
        <h3 title={product.name}>{product.name || 'Unnamed Product'}</h3>
        <p className="price">₹{(product.price || 0).toLocaleString()}</p>

        <div className="seller-info" onClick={handleSellerClick}>
          <img
            src={seller?.profilePhoto || '/default-avatar.jpg'}
            alt={seller?.name || 'Seller'}
            className="seller-avatar"
            loading="lazy"
            decoding="async"
            onError={e => { e.target.src = '/default-avatar.jpg'; }}
          />
          <div className="seller-details">
            <div className="seller-name">
              {seller?.name || 'Seller'}
              {seller?.plan !== 'free' && seller?.plan && (
                <img src={premiumBadge} alt="Premium" className="premium-badge" />
              )}
            </div>
            <div className="seller-username">@{seller?.username || 'seller'}</div>
          </div>
        </div>

        <div className="product-actions">
          <button className="chat-button" onClick={handleChat}>
            <svg viewBox="0 0 24 24">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Chat Now
          </button>
        </div>
      </div>
    </div>
  );
});
ProductCard.displayName = 'ProductCard';

/* ─────────────────────────────────────────────
   Main Gallery - FIXED
───────────────────────────────────────────── */
const ProductGallery = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('newest');

  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [sellerTypes, setSellerTypes] = useState(DEFAULT_SELLER_TYPES);
  const [dateRange, setDateRange] = useState('all');

  const toggleCategory = useCallback(key =>
    setCategories(prev => ({ ...prev, [key]: !prev[key] })), []);

  const toggleSellerType = useCallback(key =>
    setSellerTypes(prev => ({ ...prev, [key]: !prev[key] })), []);

  const clearFilters = useCallback(() => {
    setPriceRange([0, PRICE_MAX]);
    setCategories(DEFAULT_CATEGORIES());
    setSellerTypes(DEFAULT_SELLER_TYPES());
    setDateRange('all');
  }, []);

  const handleShare = useCallback(productId => {
    const url = `${window.location.origin}/product/${productId}`;
    if (navigator.share) {
      navigator.share({ title: 'Check this plant on Ankurit', url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
    }
  }, []);

  /* ── Fetch products with proper async handling ── */
  useEffect(() => {
    let isMounted = true;
    const database = getDatabase();
    const productsRef = ref(database, 'products');

    console.log('🔍 Starting to fetch products from RTDB...');

    const unsubscribe = onValue(
      productsRef,
      async (snapshot) => {
        try {
          console.log('📦 Raw snapshot received, processing...');
          
          const raw = [];
          snapshot.forEach(child => {
            const p = child.val();
            if (p.status === 'suspended') return;

            // Normalise image URLs - ensure we always have at least one
            let imageUrls = [];
            if (Array.isArray(p.imageUrls) && p.imageUrls.length > 0) {
              imageUrls = p.imageUrls.map(processImageUrl).filter(Boolean);
            } else if (p.imageUrl) {
              const u = processImageUrl(p.imageUrl);
              if (u) imageUrls = [u];
            }
            
            // Ensure every product has at least a placeholder
            if (imageUrls.length === 0) {
              imageUrls = ['/placeholder-plant.jpg'];
            }

            raw.push({
              id: child.key,
              ...p,
              status: p.status || 'active',
              rank: Number(p.rank || 0),
              adminPinned: Boolean(p.adminPinned),
              imageUrls,
              timestamp: p.timestamp || p.createdAt || Date.now(),
              price: Number(p.price) || 0,
              name: p.name || 'Unnamed Product',
            });
          });

          console.log(`✅ Processed ${raw.length} products from RTDB`);

          // Sort: pinned → rank → newest
          raw.sort((a, b) =>
            (Number(b.adminPinned) - Number(a.adminPinned)) ||
            (b.rank - a.rank) ||
            (b.timestamp - a.timestamp)
          );

          // Batch-fetch unique seller docs
          const uniqueIds = [...new Set(raw.map(p => p.sellerId).filter(Boolean))];
          console.log(`👤 Fetching ${uniqueIds.length} unique sellers...`);
          
          const sellerMap = {};
          
          // Use Promise.allSettled to avoid one failure breaking everything
          await Promise.allSettled(
            uniqueIds.map(async id => {
              try {
                const snap = await getDoc(doc(db, 'users', id));
                if (snap.exists()) {
                  const d = snap.data();
                  sellerMap[id] = {
                    name: d.fullName || d.displayName || d.name || 'Seller',
                    username: d.username || id.slice(0, 8),
                    plan: d.plan || 'free',
                    isFarmer: d.userType === 'farmer',
                    isVerified: Boolean(d.isVerified),
                    isTrusted: Boolean(d.isTrusted),
                    profilePhoto: d.profilePhotoThumb || d.profilePhoto || d.photoURL || '/default-avatar.jpg',
                  };
                } else {
                  sellerMap[id] = {
                    name: 'Seller',
                    username: id.slice(0, 8),
                    plan: 'free',
                    profilePhoto: '/default-avatar.jpg',
                  };
                }
              } catch (err) {
                console.warn(`⚠️ Failed to fetch seller ${id}:`, err);
                sellerMap[id] = {
                  name: 'Seller',
                  username: id?.slice?.(0, 8) || 'unknown',
                  plan: 'free',
                  profilePhoto: '/default-avatar.jpg',
                };
              }
            })
          );

          console.log(`✅ Loaded ${Object.keys(sellerMap).length} sellers`);

          if (!isMounted) return;
          
          setProducts(raw);
          setSellers(sellerMap);
          setLoading(false);
          setError(null);
          console.log('🎉 Products gallery loaded successfully!');
          
        } catch (err) {
          console.error('❌ Error processing products:', err);
          if (isMounted) {
            setError(err.message || 'Failed to load products');
            setLoading(false);
          }
        }
      },
      (err) => {
        console.error('❌ RTDB connection error:', err);
        if (isMounted) {
          setError(err.message || 'Failed to connect to database');
          setLoading(false);
        }
      }
    );

    // Cleanup
    return () => {
      isMounted = false;
      off(productsRef);
    };
  }, []);

  const paginate = useCallback(page => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const hasActiveFilters = useMemo(() =>
    priceRange[0] > 0 || priceRange[1] < PRICE_MAX ||
    Object.values(categories).some(Boolean) ||
    Object.values(sellerTypes).some(Boolean) ||
    dateRange !== 'all',
  [priceRange, categories, sellerTypes, dateRange]);

  const filteredProducts = useMemo(() => {
    const now = Date.now();
    const day = 86_400_000;
    const activeCategories = Object.keys(categories).filter(k => categories[k]);
    const activeSellerTypes = Object.keys(sellerTypes).filter(k => sellerTypes[k]);

    let result = products.filter(p => {
      if ((p.price || 0) < priceRange[0] || (p.price || 0) > priceRange[1]) return false;
      
      if (activeCategories.length) {
        const productCategory = (p.category || '').toLowerCase().replace(/ /g, '');
        if (!activeCategories.includes(productCategory)) return false;
      }
      
      if (activeSellerTypes.length) {
        const s = sellers[p.sellerId];
        if (!s) return false;
        const match = (sellerTypes.farmer && s.isFarmer) ||
          (sellerTypes.verified && s.isVerified) ||
          (sellerTypes.trusted && s.isTrusted);
        if (!match) return false;
      }
      
      if (dateRange === '7days' && now - (p.timestamp || 0) > 7 * day) return false;
      if (dateRange === '30days' && now - (p.timestamp || 0) > 30 * day) return false;
      return true;
    });

    if (sortBy === 'priceLow') result = [...result].sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === 'priceHigh') result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));
    // 'newest' order already set from fetch sort

    return result;
  }, [products, sellers, priceRange, categories, sellerTypes, dateRange, sortBy]);

  const displayedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [filteredProducts.length]);

  const pageButtons = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const around = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1].filter(p => p >= 1 && p <= totalPages));
    return [...around].sort((a, b) => a - b);
  }, [totalPages, currentPage]);

  const filterProps = {
    priceRange, setPriceRange,
    categories, toggleCategory,
    sellerTypes, toggleSellerType,
    dateRange, setDateRange,
    clearFilters,
  };

  // Show error state
  if (error && products.length === 0) {
    return (
      <div className="product-gallery-container">
        <div className="error-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="error-icon">⚠️</div>
          <h3>Failed to load products</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-gallery-container">

      <div className="gallery-header">
        <h2>Plant Collection</h2>
        <div className="gallery-header-actions">
          {!loading && (
            <span className="result-count">
              {filteredProducts.length} listing{filteredProducts.length !== 1 ? 's' : ''}
            </span>
          )}
          <button
            className="filter-toggle-button"
            onClick={() => setShowMobileFilters(true)}
            aria-label="Open filters"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="4" y1="12" x2="14" y2="12"/>
              <line x1="4" y1="18" x2="10" y2="18"/>
            </svg>
            Filters
            {hasActiveFilters && <span className="filter-dot" />}
          </button>
        </div>
      </div>

      <div className="gallery-content">

        <aside className="desktop-filters">
          <FilterPanel {...filterProps} />
          <button className="clear-filters" onClick={clearFilters}>Clear All Filters</button>
        </aside>

        <div className="products-container">

          {!loading && (
            <div className="sort-bar">
              <span className="sort-bar-count">
                {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''}
              </span>
              <select
                className="sort-select"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                aria-label="Sort products"
              >
                <option value="newest">Newest First</option>
                <option value="priceLow">Price: Low → High</option>
                <option value="priceHigh">Price: High → Low</option>
              </select>
            </div>
          )}

          {loading ? (
            <SkeletonGrid />
          ) : (
            <>
              <div className="products-grid">
                {displayedProducts.length > 0 ? (
                  displayedProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      seller={sellers[product.sellerId]}
                      onShare={() => handleShare(product.id)}
                      onClick={() => navigate(`/product/${product.id}`)}
                    />
                  ))
                ) : (
                  <div className="no-results">
                    <div className="no-results-icon">🌿</div>
                    <p>No plants match your filters</p>
                    <small>Try adjusting or clearing the filters</small>
                    <button onClick={clearFilters}>Clear Filters</button>
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="pagination-wrap">
                  <div className="pagination">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      ‹ Prev
                    </button>

                    {pageButtons.map((page, i) => {
                      const prevPage = pageButtons[i - 1];
                      return (
                        <React.Fragment key={page}>
                          {prevPage && page - prevPage > 1 && <span style={{ lineHeight: '38px', color: 'var(--clr-ink-muted)' }}>…</span>}
                          <button
                            onClick={() => paginate(page)}
                            className={[
                              'page-num',
                              currentPage === page ? 'active' : '',
                              Math.abs(currentPage - page) <= 1 ? 'near-active' : '',
                            ].join(' ').trim()}
                            aria-current={currentPage === page ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      Next ›
                    </button>
                  </div>
                  <p className="pagination-info">
                    Page {currentPage} of {totalPages} · {filteredProducts.length} listings
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div
        className={`mobile-filter-overlay ${showMobileFilters ? 'open' : ''}`}
        onClick={() => setShowMobileFilters(false)}
        aria-hidden="true"
      />
      {showMobileFilters && (
        <div className="mobile-filter-sheet" role="dialog" aria-label="Filters">
          <div className="sheet-handle" />
          <div className="sheet-header">
            <h3>Filters {hasActiveFilters && <span className="filter-dot" style={{ display: 'inline-block', marginLeft: 6 }} />}</h3>
            <button className="sheet-close" onClick={() => setShowMobileFilters(false)} aria-label="Close filters">✕</button>
          </div>
          <div className="sheet-body">
            <FilterPanel {...filterProps} />
          </div>
          <div className="sheet-footer">
            <button className="clear-btn" onClick={clearFilters}>Reset</button>
            <button className="apply-btn" onClick={() => setShowMobileFilters(false)}>
              Show {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
